import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebaseConfig";
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  doc,
  updateDoc,
  writeBatch,
  arrayUnion,
} from "firebase/firestore";

/**
 * Real-time hook that merges personal notifications + admin announcements
 * into a unified notification inbox.
 *
 * Returns: { notifications, unreadCount, loading, markRead, markAllRead }
 */
const NO_READ_IDS = [];

export default function useNotifications() {
  const { currentUser, client } = useAuth();
  const [personalNotifs, setPersonalNotifs] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  // Local set of announcement IDs marked read this session (avoids stale AuthContext)
  const [localReadIds, setLocalReadIds] = useState([]);

  // Stable reference: a fresh [] each render made the announcements listener re-subscribe in a loop
  const readAnnouncements = client?.readAnnouncements || NO_READ_IDS;

  // Listener 1: Personal notifications from users/{uid}/notifications
  useEffect(() => {
    if (!currentUser?.uid) {
      setPersonalNotifs([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, "users", currentUser.uid, "notifications"),
      orderBy("createdAt", "desc"),
      limit(30)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const items = snap.docs.map((d) => ({
          id: d.id,
          source: "personal",
          ...d.data(),
          createdAt: d.data().createdAt?.toDate?.() || new Date(),
        }));
        setPersonalNotifs(items);
        setLoading(false);
      },
      (err) => {
        console.warn("Notification listener error:", err.message);
        setLoading(false);
      }
    );

    return unsub;
  }, [currentUser?.uid]);

  // Listener 2: Admin announcements
  useEffect(() => {
    if (!currentUser?.uid) {
      setAnnouncements([]);
      return;
    }

    const q = query(
      collection(db, "announcements"),
      orderBy("createdAt", "desc"),
      limit(20)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const now = new Date();
        const userRole = client?.role || "learner";
        const userCreatedAt = client?.createdAt?.toDate?.() || null;

        const items = snap.docs
          .map((d) => {
            const data = d.data();
            return {
              id: d.id,
              source: "announcement",
              type: "system",
              title: data.title,
              body: data.body,
              icon: data.icon || "Megaphone",
              link: data.link || null,
              createdBy: data.createdByName || "System Admin",
              createdAt: data.createdAt?.toDate?.() || new Date(),
              expiresAt: data.expiresAt?.toDate?.() || null,
              audience: data.audience || "all",
              read: readAnnouncements.includes(d.id) || localReadIds.includes(d.id),
            };
          })
          .filter((a) => {
            // Only show announcements created after the user registered
            if (userCreatedAt && a.createdAt < userCreatedAt) return false;
            // Filter expired
            if (a.expiresAt && a.expiresAt < now) return false;
            // Filter audience
            if (a.audience === "all") return true;
            if (Array.isArray(a.audience)) return a.audience.includes(userRole);
            return true;
          });

        setAnnouncements(items);
      },
      (err) => {
        console.warn("Announcements listener error:", err.message);
      }
    );

    return unsub;
  }, [currentUser?.uid, client?.role, client?.createdAt, readAnnouncements, localReadIds]);

  // Merge both streams, sorted by createdAt desc
  const notifications = [...personalNotifs, ...announcements].sort(
    (a, b) => b.createdAt - a.createdAt
  );

  const unreadCount = notifications.filter((n) => !n.read).length;

  // Mark a single notification as read
  const markRead = useCallback(
    async (notif) => {
      if (!currentUser?.uid || notif.read) return;

      try {
        if (notif.source === "personal") {
          await updateDoc(
            doc(db, "users", currentUser.uid, "notifications", notif.id),
            { read: true }
          );
        } else if (notif.source === "announcement") {
          setLocalReadIds((prev) => [...prev, notif.id]);
          await updateDoc(doc(db, "users", currentUser.uid), {
            readAnnouncements: arrayUnion(notif.id),
          });
        }
      } catch (err) {
        console.error("Failed to mark notification read:", err);
      }
    },
    [currentUser?.uid]
  );

  // Mark all unread notifications as read
  const markAllRead = useCallback(async () => {
    if (!currentUser?.uid) return;

    try {
      // 1. Batch-update personal notifications (separate batch so a user-doc
      //    failure doesn't roll back the read marks)
      const unreadPersonal = personalNotifs.filter((n) => !n.read);
      if (unreadPersonal.length > 0) {
        const notifBatch = writeBatch(db);
        for (const notif of unreadPersonal) {
          notifBatch.update(
            doc(db, "users", currentUser.uid, "notifications", notif.id),
            { read: true }
          );
        }
        await notifBatch.commit();
      }

      // 2. Mark announcements read on the user doc (separate write)
      const unreadAnnounceIds = announcements
        .filter((a) => !a.read)
        .map((a) => a.id);

      if (unreadAnnounceIds.length > 0) {
        setLocalReadIds((prev) => [...prev, ...unreadAnnounceIds]);
        await updateDoc(doc(db, "users", currentUser.uid), {
          readAnnouncements: arrayUnion(...unreadAnnounceIds),
        });
      }
    } catch (err) {
      console.error("Failed to mark all read:", err);
    }
  }, [currentUser?.uid, personalNotifs, announcements]);

  return { notifications, unreadCount, loading, markRead, markAllRead };
}
